import React, {useEffect, useReducer, useState} from 'react';
import {useDispatch, useSelector} from "react-redux";
import {push} from "connected-react-router";
import PropTypes from 'prop-types';
import { useLocation, useParams } from "react-router-dom";
import {useTranslation} from "react-i18next";

import SettingsForm from "../settings/SettingsForm";
import {uuid} from "../../../../utilities/GeneralUtilities";

import {appSetModalContent, appSetUUID} from "../../../../redux/app/appActions";
import {findComponent, findXGridComponent} from "../../../../redux/page/pageActions";


import { styled, Tabs, Tab, Box } from '@mui/material';


const PREFIX = 'Tab';

const classes = {
    root: `${PREFIX}-root`,
    tab: `${PREFIX}-tab`
};

const Root = styled('div')((
    {
        theme
    }
) => ({
    [`&.${classes.root}`]: {
        height: "100%",
        width: "100%",
        overflow: "hidden",
		backgroundColor: theme.palette.background.default
    },

    [`& .${classes.tab}`]: {
        minWidth: "160px",
		backgroundColor: theme.palette.background.default
    }
}));

function TabPanel(props) {
    const { children, value, index, ...other } = props;

    // NOTE: We may still want to allow
    // Parent/Paper stying of the tab however this is
    // currently not implemented
    return (
        <div
            style={{height: 'calc(100% - 48px)', width: '100%', overflow: 'auto'}}
            role="tabpanel"
            hidden={value !== index}
            id={`wrapped-tabpanel-${index}`}
            aria-labelledby={`wrapped-tab-${index}`}
            {...other}
        >
            {value === index && <Box style={{height: '100%', width: '100%', padding: '0px'}}>{children}</Box>}
        </div>
    );
}

TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.any.isRequired,
    value: PropTypes.any.isRequired,
};

export default function SmartTab({ configuration, parseComponent, level, xId }) {
    const dispatch = useDispatch();
    const { t, i18n } = useTranslation();

    // App State
    const activeRoute = useSelector(state => state.router.location.pathname);
    const user = useSelector(state => state.user);
    const dataSources = useSelector(state => state.data.dataSources);
	const pageID = useSelector(state => state.app.pageID);
	const pages = useSelector(state => state.page.pages);
	const pageConfigs = useSelector(state => state.config.data.pageConfigs);
	const editXId = useSelector(state => state.page.componentEdit.xId);
	const quickState = useSelector(state => state.page.quickState);
	const componentUpdated = useSelector(state => state.page.componentUpdated);
	const activeRoleName = useSelector(state => state.persist.activeRoleName);

    // Component State
	const [tabConfig, setTabConfig] = useState(configuration);
	const [futureTabConfig, setFutureTabConfig] = useState(configuration);
    const [tabComponents, setTabComponents] = useState([]);
    const [tabPanels, setTabPanels] = useState([]);
    const [activeTab, setActiveTab] = useState({index: 0, name: null});
    const [tabAlignTop, setTabAlignTop] = useState(!!configuration.element.alignTop);
    const [syncRoutes, setSyncRoutes] = useState(!!configuration.element.syncRoutes);
    const [color, setColor] = useState(configuration.element?.color !== undefined ? ((configuration.element.color.toLowerCase() === 'primary' || configuration.element.color.toLowerCase() === 'secondary') ? configuration.element.color : "primary") : "primary");
    const [dynamicTabs, setDynamicTabs] = useState(null);
    // Used to access tabs when dynamic data sources are evaluated
    const [dynamicTabStateCount, setDynamicTabStateCount] = useState(null);
    // Used to access tabs (once) when there are no dynamic data sources
    const [noDynamicTabsStateCount, setNoDynamicTabsStateCount] = useState(null);

    // Get active location of app
    let location = useLocation();

	// When the configuration changes - cache our known dynamic tabs
	useEffect(() => {
		// console.log('tab:useEffect() => {},[configuration])', configuration);

		setTabConfig(configuration);
		setDataDrivenTabs(configuration);

	},[configuration]);

	// Titles & Resized
	useEffect(() => {
		if (xId) {
			if (componentUpdated.id === xId) {
				// console.log('TAB componentUpdated: ', componentUpdated.config);
				setTabConfig(componentUpdated.config);
				setFutureTabConfig(componentUpdated.config);
				setDataDrivenTabs(componentUpdated.config);
			}
		}
	}, [componentUpdated]);

	// xEdit
	useEffect(() => {
		if (editXId) {
			if ((editXId === xId) && (pageID)) {

				let placeholders = {}
				for (let i = 0; i < tabConfig.tabs.length; i++) {
					let labelVariable = "label" + i;
					placeholders[labelVariable] = tabConfig.tabs[i].label;
				}

				const settingsConfig = {
					title: 'Tab Configuration',
					parent: {
						minWidth: '400px',
						maxWidth: '400px',
						minHeight: '520px',
						maxHeight: '520px'
					},
					paper: null,
					button: null,
					content: <SettingsForm pageID={pageID} xId={xId} configuration={tabConfig} schemaType={'tab'} placeholders={placeholders}/>,
					tooltipContent: null
				}
				dispatch(appSetModalContent(settingsConfig));
			}
		}
	},[editXId, pageID]);

	// Did Child Components Trigger Redux State Change?
	useEffect(() => {
		if (quickState.xId) {
			const valTabConfig = JSON.parse(JSON.stringify(tabConfig));   // Save tabConfig "By Value"
			const page = pages.find(page => page.name === pageID);

			// Each page has 3 potential areas, need to loop through each one to find our xGrid
			// break when found
			for (let i = 0; i < pageConfigs.length; i++ ) {
				const roleConfig = page.roles.find(r => r.name === activeRoleName)[pageConfigs[i]];
				if (roleConfig) {

					if (quickState.action.toLowerCase() === 'layout') {

						// Find the current xGrid
						const xGridComponent = findXGridComponent(quickState.xId, roleConfig);

						if (xGridComponent) {

							// Find the Component within tabConfig and then update tabConfig
							const foundComponent = findComponent(quickState.xId, null, valTabConfig.components);

							if (foundComponent) {
								foundComponent.configuration.layouts = xGridComponent.configuration.layouts;
								setFutureTabConfig(valTabConfig);	// If we were to setTabConfig now rerenders would occur, so we'll prep it for tab changes in handleChanges

								break;
							}
						}
					} else if (quickState.action.toLowerCase() === 'config') {

						// Find the updated Component within Pages then update tabConfig
						const foundComponent = findComponent(quickState.xId, null, roleConfig.configuration.components);

						// Find the Component within tabConfig
						const foundResult = findComponent(quickState.xId, null, valTabConfig.components);

						if (foundComponent && foundResult) {
							foundResult.configuration = foundComponent.configuration;
							setFutureTabConfig(valTabConfig);	// If we were to setTabConfig now rerenders would occur, so we'll prep it for tab changes in handleChanges

							break;
						}
					}

				}
			}
		}
	},[quickState.currState]);

    // When the location changes determine the active tab
    useEffect(() => {
        // console.log('tab:useEffect() => {},[location.pathname])', location);

        if (!syncRoutes)
            return;

        // We want to ensure there is a tab in the url... if not
        // we'll push one in HOWEVER we also need to maintain any query
        // parameters
        const pathParts = activeRoute.split('/').filter(p => p.length > 0);

        // The last part would be a name match
        const potentialTab = pathParts[pathParts.length - 1];

        setActiveTab({index: -1, name: potentialTab.toLowerCase()});
    }, [location.pathname]);

    // Once we have assessed if tabs are dynamic or not
    // OR their state has changed we build tab panels
    useEffect(() => {
        // console.log('tab:useEffect(() => {},[dynamicTabStateCount, noDynamicTabsStateCount, tabConfig])');

        if (!dynamicTabs)
            return;

        const roleFeatures = user.roleFeatures;

        const tcs = [];
        const tps = [];

        for (let i = 0; i < tabConfig.tabs.length; i++) {
            const tab = tabConfig.tabs[i];
            const comp = i < tabConfig.components.length ? parseComponent(tabConfig.components[i], false, level, false, null, null, null) : <React.Fragment/>;

            // Determine if this feature is enabled
            let addTab = true;
            if (tab.featureID) {
                addTab = false;
                const features = roleFeatures.filter(feature => feature.featureType === tab.featureID);
                if (features.length === 1 && features[0].enabled && tab.enabled === true) {
                    addTab = true;
                }
            }

            // Is the tab dynamic? Loaded & Visible
            if (tab.dataSource ) {
				const dynamicTab = dynamicTabs.find(dt => ((dt.dataSource.toLowerCase() === tab.dataSource.name.toLowerCase()) && (dt.value.toLowerCase() === tab.dataSource.value.toLowerCase())))

                if (!dynamicTab.loaded || !dynamicTab.visible || tab.enabled === false)
                    addTab = false;
            }

            if (addTab) {
                // console.log('Adding {0} with value {1}'.format(tab.label, tps.length));
                tcs.push(<Tab key={tcs.length} className={classes.tab} label={t(tab.label)} disabled={tab.enabled !== true ? true : false}></Tab>);
                tps.push(<TabPanel key={tps.length}
									data-name={tab.label.toLowerCase()}
									index={activeTab.index}
									value={tps.length}>
							{comp}
						</TabPanel>);
            }
        }

        setTabComponents(tcs);
        setTabPanels(tps);

    }, [dynamicTabStateCount, noDynamicTabsStateCount, tabConfig]);

    // When the data sources change we need to determine
    // if our dynamicTabs needs to be displayed
    useEffect(() => {
        // console.log('tab: useEffect(() => {},[dataSources])');

        if (!dynamicTabs)
            return;

        // If none are data dependant then release the hounds
        if (dynamicTabs.length === 0)
        {
            // console.log(noDynamicTabsStateCount);
            if (noDynamicTabsStateCount !== 0)
                setNoDynamicTabsStateCount(0);
            return;
        }

        let changed = false;
        for (const tab of dynamicTabs) {
            const dsName = tab.dataSource;
            const ds = dataSources.find(s => s.name.toLowerCase() === dsName);

            // Flag what has gone into loading state
            if (!ds.fetched === true) {
                // NOTE: Disable tab?... it may be going bye bye
                if (tab.loaded === false)
                    changed = true;

                tab.loaded = false;
            } else if (ds.fetched === true) {

                if (!tab.loaded)
                    changed = true;
                tab.loaded = true;

                let visible = false;
                // Does any item in the data source support this tab condition??

				if (tab?.value !== undefined) {
					if (ds.data.filter(d => d[tab.field] === tab.value).length > 0) {
						visible = true;
					}
				} else if (ds.data.filter(d => d[tab.field] === true).length > 0) {
                    visible = true;
                }

                if (tab.visible !== visible)
                    changed = true;

                tab.visible = visible;
            }
        }

        // Trigger a change when all the dynamic tabs are ready
        if (dynamicTabs.length > 0) {
            if (dynamicTabs.filter(t => t.loaded === false).length > 0)
                return;

            if (changed) {
                setDynamicTabStateCount(dynamicTabStateCount + 1);
            }
        }

    }, [dynamicTabs, dataSources]);

    // Once our tabs are updated then validate the active tab
    useEffect(() => {
        // console.log('tab: useEffect(() => {},[tabPanels])', tabPanels);

        // If we had a location change then we cannot
        // come off a route until we know if it's valid i.e.
        // a dynamic tab that is still loading
        if (dynamicTabs && dynamicTabs.length > 0 && dynamicTabs.filter(dt => !dt.loaded).length > 0)
            return;

        validateAndSetActiveTab();
    },[tabPanels]);

	// Active Tab changes, validateAndSetActiveTab
    useEffect(() => {
       // console.log('tab: useEffect(() => {},[activeTab])', activeTab);

		// Loop through tabs
		for (let i = 0; i < tabConfig.tabs.length; i++) {
			if (activeTab.index === i) {

				// Find if a global UUID should be set
				if (tabConfig.tabs[i].setUUID === true) {
					const newGuid = uuid();
					dispatch(appSetUUID(newGuid));
				}
			}
		}

        validateAndSetActiveTab();
    }, [activeTab]);

    const handleChange = (event, newValue) => {
		setTabConfig(futureTabConfig);
        setActiveTabByIndex(newValue, true);
    };

    const validateAndSetActiveTab = () => {
        // console.log('tab:validateAndSetActiveTab()', activeTab);

        // If we have a name use it
        if (activeTab.name !== null) {
            if (!tabPanels.find(t => t.props["data-name"] === activeTab.name)) {
                // Name not found so we're resetting to 0 and updating url
                // console.log('Defaulting to details, count not find ' + activeTab.name, tabPanels);
                setActiveTabByIndex(0, true);
            } else {
                // Tab was found - lookup valid index...
                const validIndex = tabPanels.findIndex(t => t.props["data-name"] === activeTab.name);

                if (activeTab.index === -1 || activeTab.index !== validIndex) {

                    // Setup a validate active tab object
                    setActiveTabByIndex(validIndex, false);
                }
            }
        } else {
            // Reset active tab by index and update i.e. projects -> projects/details
            setActiveTabByIndex(activeTab.index, true);
        }
    }

    const setActiveTabByIndex = (tabIndex, updateURL) => {
        // console.log('tab:setActiveTabByIndex()', tabIndex, updateURL);

        if (tabPanels.length > 0 && tabIndex > tabPanels.length - 1) {
            // Reset to 0
            setActiveTab({index: tabIndex, name: tabPanels[0].props["data-name"]});
            // We need tab values updated with the active tab index so increment state
            setDynamicTabStateCount(dynamicTabStateCount + 1);
        } else if (tabPanels.length > 0) {
            setActiveTab({index: tabIndex, name: tabPanels[tabIndex].props["data-name"]});
            // We need tab values updated with the active tab index so increment state
            setDynamicTabStateCount(dynamicTabStateCount + 1);
        }

        if (tabPanels.length > 0 && updateURL && syncRoutes) {
            const pathParts = activeRoute.split('/').filter(p => p.length > 0);

            const lastPart = pathParts[pathParts.length - 1].toLowerCase();
            let baseURL = null;

            const allTabLabels = tabConfig.tabs.map(t => t.label.toLowerCase());

            // Pop off the tab??
            if (allTabLabels.includes(lastPart)) {
                pathParts.pop();
                baseURL = pathParts.join('/');
            } else {
                // lastPart is not in tabs, so just set baseURL to all path parts
				baseURL = pathParts.join('/');
            }

            // Strip off trailing /
            if (baseURL && baseURL.substring(baseURL.length - 1) === '/') {
                baseURL = baseURL.substring(0, baseURL.length - 1);
            }

            const searchParams = new URLSearchParams(location.search);
            let id = searchParams.get('id');

            const name = tabPanels[tabIndex].props["data-name"];

            if (id) {
                const newRoute = '/{0}/{1}?id={2}'.format(baseURL, name, id);
                dispatch(push(newRoute));
            } else {
                const newRoute = '/{0}/{1}'.format(baseURL, name);
                dispatch(push(newRoute));
            }
        }
    }

	const setDataDrivenTabs = (tabConfiguration) => {
		const dataDrivenTabs = [];

		for (let i = 0; i < tabConfiguration.tabs.length; i++) {
			const tab = tabConfiguration.tabs[i];

			if (tab.dataSource) {
				const dynamicTab = {
					dataSource: tab.dataSource.name.toLowerCase(),
					field: tab.dataSource.field.toLowerCase(),
					value: tab.dataSource?.value !== undefined ? tab.dataSource.value : undefined,
					loaded: false,
					visible: false
				}

				dataDrivenTabs.push(dynamicTab);
			}
		}

		setDynamicTabs(dataDrivenTabs);
	}

    return (
        <Root className={classes.root}>
            {tabComponents && activeTab.index > -1 &&
				<React.Fragment>
					{!tabAlignTop &&
						<React.Fragment>
							{tabPanels}
						</React.Fragment>
					}
					{tabComponents.length > 1 &&
						<Tabs
							className={classes.tab}
							value={activeTab.index}
							indicatorColor={color}
							textColor={color}
							onChange={handleChange}
							scrollButtons={true}
							allowScrollButtonsMobile
							selectionFollowsFocus
							variant="scrollable"
							aria-label="tabs"
						>
							{tabComponents}
						</Tabs>
					}
					{tabAlignTop &&
						<React.Fragment>
							{tabPanels}
						</React.Fragment>
					}
				</React.Fragment>
            }
        </Root>
    );
}
